import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface ApplicationRequiredProps {
  title?: string;
  message?: string;
}

export default function ApplicationRequired({ title, message }: ApplicationRequiredProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center min-h-[500px] px-4">
      {/* Icon */}
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 blur-3xl opacity-20 animate-pulse"></div>
        <div className="relative bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-3xl p-8 shadow-lg">
          <svg
            className="w-24 h-24 text-indigo-600 dark:text-indigo-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="text-center max-w-lg">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          {title || t('applicationRequired.title')}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
          {message || t('applicationRequired.message')}
        </p>

        {/* Ações: criar nova ou escolher existente */}
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            onClick={() => navigate('/settings/applications?new=1')}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/30 transition-all hover:bg-indigo-700 hover:shadow-indigo-500/40"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('labels.createApplication', 'Criar nova aplicação')}
          </button>
          <button
            onClick={() => navigate('/settings/applications')}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {t('labels.manageApplications', 'Ver aplicações')}
          </button>
        </div>

        {/* Dica: usar o seletor no topo para alternar entre apps existentes */}
        <div className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          <span>{t('applicationRequired.hint')}</span>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-indigo-200/20 to-purple-200/20 dark:from-indigo-800/10 dark:to-purple-800/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-purple-200/20 to-pink-200/20 dark:from-purple-800/10 dark:to-pink-800/10 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
}
